/**
 * MetricExplainer — the single contextual-help pattern for metrics.
 *
 * Replaces the native `title=` attributes (invisible on touch, unstyled) with
 * the product Tooltip, and gives the future analytics metrics a place to say
 * what they measure, how to read them, how they are computed and what the data
 * coverage is. No metric content is shipped here: this is the pattern only.
 */
import { HelpCircle } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

/** Plain contextual help on any label. */
export function HelpTip({ children, label, side = "top", testId = "help-tip" }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            aria-label={label || "Informazioni"}
            className="inline-flex shrink-0 items-center justify-center w-10 h-10 -m-2 rounded-md text-content-muted
                       hover:text-content transition-colors duration-fast ease-system
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-left">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * @param {string} name        metric name
 * @param {string} what        what it measures
 * @param {string} [how]       how to read it
 * @param {string} [formula]   how it is computed
 * @param {string} [coverage]  data coverage / limits
 */
export function MetricExplainer({
  name, what, how, formula, coverage, side = "top", testId = "metric-explainer",
}) {
  return (
    <HelpTip label={`Cos'è ${name}`} side={side} testId={testId}>
      <div className="space-y-1.5">
        <p className="text-label uppercase text-content-muted">{name}</p>
        <p className="text-xs text-content">{what}</p>
        {how && <p className="text-xs text-content-secondary">{how}</p>}
        {formula && (
          <p className="text-code text-content-secondary bg-surface-subtle rounded px-1.5 py-1">
            {formula}
          </p>
        )}
        {coverage && <p className="text-xs text-content-muted">{coverage}</p>}
      </div>
    </HelpTip>
  );
}

export default MetricExplainer;
